// Same values as ALERTS_ASYNC_STATUS. Literals keep this module importable
// from node --test; constants.js pulls in the app bundle.
const ACTIVITY_NOTIFICATION_STATUS_SUCCESS = "success";
const ACTIVITY_NOTIFICATION_STATUS_ERROR = "error";

/**
 * Finished activities are the only ones with a per-item Descartar button.
 * In-progress items (loading / generating) stay in the list.
 * @param {Object} notification
 * @returns {boolean}
 */
export const isDismissableActivityNotification = (notification) =>
  notification?.status === ACTIVITY_NOTIFICATION_STATUS_SUCCESS ||
  notification?.status === ACTIVITY_NOTIFICATION_STATUS_ERROR;

/**
 * Descartar todas is visible only when the list has a finished activity.
 * Empty lists and in-progress-only lists hide the button.
 * @param {Array} notifications
 * @returns {boolean}
 */
export const hasDismissableActivityNotifications = (notifications = []) =>
  notifications.some(isDismissableActivityNotification);

/**
 * Same id the per-item Descartar button passes to onClose.
 * @param {Object} notification
 * @returns {string|null}
 */
export const getActivityNotificationDismissId = (notification) =>
  notification?.id || notification?.jobID || notification?.campaignID || null;

/**
 * Determines which view is currently active based on modal states
 * @param {Object} modalStates - Object containing modal open states
 * @returns {string} Active view identifier
 */
export const getActiveView = (modalStates) => {
  const { isModalCreateWithIA, isModalGallery } = modalStates;
  if (isModalCreateWithIA) return "modalCreateWithIA";
  if (isModalGallery) return "modalGallery";
  return "navbar";
};

/**
 * Creation-time sort key for a notification (ms). Used for panel ordering;
 * does not change on status/progress updates unless timestamp is explicitly set.
 * @param {Object} notification
 * @returns {number}
 */
export const getNotificationRecencyTimestamp = (notification) => {
  const ts = notification?.creationTime ?? notification?.timestamp;
  if (typeof ts === "number" && Number.isFinite(ts)) return ts;
  if (typeof ts === "string") {
    const parsed = Date.parse(ts);
    if (Number.isFinite(parsed)) return parsed;
  }
  return 0;
};

/**
 * Most recent notification first (descending creation timestamp).
 * @param {Array} notifications
 * @returns {Array}
 */
export const sortNotificationsByRecency = (notifications = []) =>
  [...notifications].sort(
    (a, b) =>
      getNotificationRecencyTimestamp(b) - getNotificationRecencyTimestamp(a)
  );

/**
 * Detects if there are new alerts that weren't present before
 * @param {Array} activeAlerts - Current active alerts
 * @param {Array} previousAlerts - Previously tracked alerts
 * @returns {boolean} True if there are new alerts
 */
export const hasNewAlerts = (activeAlerts, previousAlerts) => {
  const previousAlertIds = new Set(previousAlerts.map((alert) => alert.id));
  return activeAlerts.some((alert) => !previousAlertIds.has(alert.id));
};

/**
 * Detects if any alert has changed status
 * @param {Array} activeAlerts - Current active alerts
 * @param {Array} previousAlerts - Previously tracked alerts
 * @returns {boolean} True if any alert status changed
 */
export const hasStatusChanges = (activeAlerts, previousAlerts) => {
  return activeAlerts.some((newAlert) => {
    const oldAlert = previousAlerts.find((alert) => alert.id === newAlert.id);
    return oldAlert && oldAlert.status !== newAlert.status;
  });
};

/**
 * Checks if alerts have changed (new alerts or status changes)
 * @param {Array} activeAlerts - Current active alerts
 * @param {Array} previousAlerts - Previously tracked alerts
 * @returns {boolean} True if alerts have changed
 */
export const hasAlertsChanged = (activeAlerts, previousAlerts) => {
  return (
    hasNewAlerts(activeAlerts, previousAlerts) ||
    hasStatusChanges(activeAlerts, previousAlerts)
  );
};
